import { useState, useEffect, useCallback } from 'react'

import { useLazyQuery } from '@apollo/client'
import { ActionIcon, Tabs, Tooltip } from '@mantine/core'

import { navigate, useParams, useLocation, back } from '@redwoodjs/router'
import { Metadata } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import BulkUploadDocumentProgressNotification from 'src/components/BulkUploadDocumentProgressNotification'
import SkeletonLoader from 'src/components/Feedback/SkeletonLoader/SkeletonLoader'
import SwitchCompanyModal from 'src/components/Overlays/SwitchCompanyModal/SwitchCompanyModal'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { ActivityLogSidebarContextProvider } from 'src/context/ActivityLogSidebarContext'
import { useListOfApplicantsContext } from 'src/context/ApplicantListContext'
import { useCurrentCompanyContext } from 'src/context/CurrentCompanyContext'
import {
  usePhoneDevice,
  usePhoneDeviceDispatch,
} from 'src/context/PhoneDeviceContext'
import { useTaskModalContext } from 'src/context/TaskModalContext'
import { GET_APPLICANT_BY_ID } from 'src/graphql/applicants.graphql'
import { GET_SIDEBAR_CHECKLIST_STEPS } from 'src/graphql/steps.graphql'
import { RoleType } from 'src/graphql/types/employees'
import IconChevronLeft from 'src/icons/IconChevronLeft'
import IconClipboardList from 'src/icons/IconClipboardList'
import IconClock from 'src/icons/IconClock'
import IconDocument from 'src/icons/IconDocument'
import IconEmployment from 'src/icons/IconEmployment'
import IconFileCabinet from 'src/icons/IconFileCabinet'
import IconGraduationCap from 'src/icons/IconGraduationCap'
import { PhoneDeviceActionType } from 'src/lib/phone-device.reducer'
import { cn } from 'src/utils'

import { useApplicantCompanySwitch } from './hooks/useApplicantCompanySwitch'
import ApplicantSidebar from './sidebar/ApplicantSidebar'
import ActivityLogTab from './tabs/ActivityLogTab/ActivityLogTab'
import ApplicationTabWrapper from './tabs/ApplicationTab/ApplicationTabWrapper'
import DebugTab from './tabs/DebugTab/DebugTab'
import DocumentsTab from './tabs/DocumentsTab/DocumentsTab'
import OrdersTab from './tabs/OrdersTab/OrdersTab'
import TrainingTab from './tabs/TrainingTab/TrainingTab'
import VoETab from './tabs/VoETab/VoETab'

const TAB_ICON_CLASS = 'h-3.5 w-3.5 fill-none stroke-current'
const NAV_ICON_CLASS = 'h-3 w-3 fill-none stroke-doubleNickel-gray-500'

const APPLICANT_TABS = [
  {
    value: 'application',
    label: 'Application',
    hint: 'Application form',
    Icon: IconDocument,
  },
  {
    value: 'documents',
    label: 'Documents',
    hint: 'Uploaded documents',
    Icon: IconFileCabinet,
  },
  {
    value: 'orders',
    label: 'Orders',
    hint: 'Screening orders',
    Icon: IconClipboardList,
  },
  {
    value: 'voe',
    label: 'VoE',
    hint: 'Employment verifications',
    Icon: IconEmployment,
  },
  {
    value: 'training',
    label: 'Training',
    hint: 'Courses',
    Icon: IconGraduationCap,
  },
  {
    value: 'activitylog',
    label: 'Activity log',
    hint: 'Activity history',
    Icon: IconClock,
  },
]

const ApplicantDetailsPage = ({ id: applicantId }) => {
  const { currentUser } = useAuth()
  const isAdmin = currentUser?.roles?.includes(RoleType.ADMIN)
  const dispatchPhoneAction = usePhoneDeviceDispatch()
  const phoneDeviceContext = usePhoneDevice()
  const currentCompany = useCurrentCompanyContext()
  const { applicantList } = useListOfApplicantsContext()
  const {
    handleApplicantNotFound,
    switchTarget,
    isSwitching,
    confirmSwitch,
    cancelSwitch,
  } = useApplicantCompanySwitch(applicantId)

  const onError = useCallback(() => {
    toast('Unable to get applicant, please try again.', 'error')
  }, [])

  const refetchQueries = [
    {
      query: GET_APPLICANT_BY_ID,
      variables: {
        id: applicantId,
      },
    },
    {
      // Keeps the sidebar checklist in sync — e.g. placing an order can
      // attach documents to steps. Split from GET_APPLICANT_BY_ID so the
      // sidebar header stays on a lean, fast query.
      query: GET_SIDEBAR_CHECKLIST_STEPS,
      variables: {
        applicantId,
      },
    },
  ]

  const [getApplicant, { data: applicantInfoData }] = useLazyQuery(
    GET_APPLICANT_BY_ID,
    {
      variables: {
        id: applicantId,
      },
      onError,
    }
  )

  const applicantInfo = applicantInfoData?.applicant || null
  const pageTitle = applicantInfo
    ? `${applicantInfo.firstName} ${applicantInfo.lastName}`
    : 'Applicant Details'
  const defaultTab = 'application'
  const { tab, activityType, dqf = undefined } = useParams()

  const applicantIndex = applicantList?.indexOf(applicantId) ?? -1
  const isFirstApplicant = applicantIndex === 0
  const isLastApplicant = applicantIndex === applicantList?.length - 1
  const goToApplicantAt = (index: number) => {
    const targetApplicantId = applicantList?.[index]
    if (targetApplicantId) {
      navigate(`/applicants/${targetApplicantId}/${tab}`)
    }
  }

  // Bumped when the already-active Documents tab is clicked, so an open
  // document closes and the tab lands on its checklist overview
  const [documentsTabReclicks, setDocumentsTabReclicks] = useState(0)

  const tabComponents = {
    application: (
      <ApplicationTabWrapper
        applicantId={applicantId}
        applicantInfo={applicantInfo}
        onRender={refetchQueries}
        onApplicantNotFound={handleApplicantNotFound}
      />
    ),
    documents: (
      <DocumentsTab
        applicantId={applicantId}
        dqf={dqf}
        applicantInfo={applicantInfo}
        tabReclickSignal={documentsTabReclicks}
      />
    ),
    voe: <VoETab applicantId={applicantId} applicant={applicantInfo} />,
    orders: <OrdersTab applicantId={applicantId} onRender={refetchQueries} />,
    activitylog: (
      <ActivityLogTab applicant={applicantInfo} activityType={activityType} />
    ),
    training: <TrainingTab />,
    ...(isAdmin && {
      debug: <DebugTab applicantId={applicantId} applicant={applicantInfo} />,
    }),
  }

  const location = useLocation()
  const currentTab = tabComponents[tab] ? tab : defaultTab
  const [activeTab, setActiveTab] = useState<string | null>(currentTab)
  const taskModal = useTaskModalContext()

  useEffect(() => {
    const pathParts = location.pathname.split('/')
    const newTab = pathParts[3]

    if (tabComponents[newTab]) {
      setActiveTab(newTab)
    } else {
      setActiveTab(defaultTab)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location])

  useEffect(() => {
    getApplicant()
  }, [activeTab, applicantId])

  // Deep-link: open the task modal when ?taskId is present
  useEffect(() => {
    if (!taskModal) return
    const params = new URLSearchParams(location.search)
    const taskIdFromUrl = params.get('taskId')
    if (!taskIdFromUrl) return
    const alreadyOpenForSameTask =
      taskModal.opened && taskModal.taskId === taskIdFromUrl
    if (!alreadyOpenForSameTask) {
      taskModal.handleOpen?.({
        id: taskIdFromUrl,
        applicant: applicantInfo || null,
      })
    }
  }, [location.search, taskModal?.opened, taskModal?.taskId, applicantInfo])

  useEffect(() => {
    if (applicantInfoData && !applicantInfoData.applicant) {
      handleApplicantNotFound()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantInfoData])

  const handleTabChange = (newTab) => {
    if (tabComponents[newTab]) {
      setActiveTab(newTab)
      navigate(`/applicants/${applicantId}/${newTab}`)
    }
  }

  const onPhoneCall = useCallback(
    async ({ applicantId, applicantFullName, phone }) => {
      if (phoneDeviceContext?.phoneDevice?.isBusy) {
        return
      }
      dispatchPhoneAction({
        type: PhoneDeviceActionType.CALL,
        payload: {
          applicantId,
          phone,
          applicantFullName,
        },
      })
    },
    [phoneDeviceContext?.phoneDevice?.isBusy]
  )

  const switchCompanyModal = (
    <SwitchCompanyModal
      opened={!!switchTarget}
      applicantName={`${switchTarget?.applicantFirstName ?? ''} ${
        switchTarget?.applicantLastName ?? ''
      }`.trim()}
      companyName={switchTarget?.companyName ?? ''}
      isSwitching={isSwitching}
      onCancel={cancelSwitch}
      onConfirm={confirmSwitch}
    />
  )

  if (applicantInfoData && !applicantInfoData.applicant) {
    return (
      <>
        {switchCompanyModal}
        <SkeletonLoader />
      </>
    )
  }

  return (
    <ActivityLogSidebarContextProvider>
      {switchCompanyModal}
      <div className="flex h-full flex-col gap-3 px-2">
        <Metadata title={pageTitle} description="ApplicantDetails page" />
        <div className="flex h-full flex-row-reverse gap-3 overflow-hidden">
          <div className="flex h-full w-[27vw] flex-col gap-4 border-l-[1px] p-2">
            <ApplicantSidebar
              key={applicantId}
              applicantId={applicantId}
              showNavigation={false}
              onPhoneCall={onPhoneCall}
            />
          </div>
          <div className="flex flex-1 flex-col gap-5">
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="pills"
              classNames={{
                root: 'h-full',
                list: 'items-center gap-0.5 rounded-xl border border-doubleNickel-gray-200 bg-white px-2 py-2 shadow-sm',
                tab: 'h-8 rounded-lg px-2.5 text-[13px] font-medium text-doubleNickel-gray-600 hover:bg-doubleNickel-gray-50 [&[data-active]]:bg-doubleNickel-gray-100 [&[data-active]]:font-semibold [&[data-active]]:text-doubleNickel-gray-900',
                tabSection:
                  'text-doubleNickel-gray-500 ![--tab-section-margin-right:6px] [[data-active]_&]:text-doubleNickel-gray-700',
              }}
            >
              <Tabs.List>
                <Tooltip label="Applicants page">
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="md"
                    radius="md"
                    aria-label="Applicants page"
                    onClick={() => {
                      const mostRecentApplicantListURL = localStorage.getItem(
                        'latestUrlWithFilteredApplicants'
                      )
                      if (mostRecentApplicantListURL) {
                        const url =
                          '/' + mostRecentApplicantListURL.split('/')[3]
                        navigate(url)
                      } else {
                        back()
                      }
                    }}
                  >
                    <IconChevronLeft className={NAV_ICON_CLASS} />
                  </ActionIcon>
                </Tooltip>
                <div className="mx-1.5 h-5 w-px bg-doubleNickel-gray-200" />
                {APPLICANT_TABS.map(({ value, label, hint, Icon }) =>
                  value === 'training' &&
                  !currentCompany?.trainingEnabled ? null : (
                    <Tooltip key={value} label={hint} openDelay={400}>
                      <Tabs.Tab
                        value={value}
                        leftSection={<Icon className={TAB_ICON_CLASS} />}
                        onClick={() => {
                          // Tabs only fires onChange when the tab changes
                          if (value === 'documents' && activeTab === value) {
                            setDocumentsTabReclicks((n) => n + 1)
                          }
                        }}
                      >
                        {label}
                      </Tabs.Tab>
                    </Tooltip>
                  )
                )}
                {applicantIndex !== -1 && (
                  <div className="ml-auto flex items-center gap-0.5">
                    <Tooltip
                      label={
                        isFirstApplicant
                          ? 'No previous applicants on the current page'
                          : 'Previous applicant'
                      }
                    >
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="md"
                        radius="md"
                        aria-label="Previous applicant"
                        className={cn(
                          isFirstApplicant && 'opacity-50 hover:bg-transparent'
                        )}
                        onClick={() => goToApplicantAt(applicantIndex - 1)}
                      >
                        <IconChevronLeft className={NAV_ICON_CLASS} />
                      </ActionIcon>
                    </Tooltip>
                    <span className="min-w-[44px] text-center text-xs font-medium tabular-nums text-doubleNickel-gray-600">
                      {applicantIndex + 1} / {applicantList.length}
                    </span>
                    <Tooltip
                      label={
                        isLastApplicant
                          ? 'No more applicants on the current page'
                          : 'Next applicant'
                      }
                    >
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="md"
                        radius="md"
                        aria-label="Next applicant"
                        className={cn(
                          isLastApplicant && 'opacity-50 hover:bg-transparent'
                        )}
                        onClick={() => goToApplicantAt(applicantIndex + 1)}
                      >
                        <IconChevronLeft
                          className={cn(NAV_ICON_CLASS, 'rotate-180')}
                        />
                      </ActionIcon>
                    </Tooltip>
                  </div>
                )}
              </Tabs.List>
              <Tabs.Panel
                value={activeTab}
                className="h-full px-3 pt-5"
                key={applicantId}
              >
                {tabComponents[activeTab]}
              </Tabs.Panel>
            </Tabs>
            <BulkUploadDocumentProgressNotification />
          </div>
        </div>
      </div>
    </ActivityLogSidebarContextProvider>
  )
}

export default ApplicantDetailsPage
